// Содержимое попапа кнопки аккаунта: язык, поездки, тарифы, настройки,
// персонализация, выход. Сам попап (позиционирование, клик мимо, Esc) держит
// AccountButton — этот компонент только про то, что внутри.
//
// Тот же список пунктов используется и в едином мобильном сайдбаре
// (MobileShellHeader), но там выход прибит к низу панели отдельным футером, а
// не идёт последней строкой списка — на весь экран сайдбара строка потерялась
// бы среди остальных при скролле. Поэтому у выхода два пути наружу: прячется
// внутри списка пропом `hideSignOut`, а сама логика выхода — в
// `useSignOutAction`, которым пользуется и футер сайдбара, чтобы оба места
// вели себя одинаково (один и тот же редирект с /trips), а не разошлись.
import type { ReactElement } from 'react';
import { LanguagePicker, useT } from '../i18n';
import { useTrips } from '../trips';
import { navigate, useRoute } from '../router';
import { useAuth } from './AuthProvider';
import styles from './AccountMenu.module.css';

export interface AccountMenuProps {
  onClose(): void;
  onOpenSettings(): void;
  onOpenPersonalization(): void;
  /** Развёрнутый вид внутри уже открытой панели — без своей рамки и тени. */
  inline?: boolean;
  /** Не рисовать пункт «Выйти»: он рендерится снаружи (см. MobileShellHeader). */
  hideSignOut?: boolean;
}

export function SignOutIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" />
      <path d="M10 12h10M17 8l4 4-4 4" />
    </svg>
  );
}

/** Щит — тот же смысл, что и в шапке /admin: доступ, за которым нужно право. */
function AdminIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3.5 5 6v5.5c0 4.2 2.9 7.4 7 8.5 4.1-1.1 7-4.3 7-8.5V6z" />
      <path d="M9 12l2 2 4-4.5" />
    </svg>
  );
}

/** Чемодан с перемычкой-ручкой, читающейся и как строка списка поездок. */
function TripsIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="7" width="18" height="13" rx="2.4" />
      <path d="M9 7V5.6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2V7" />
      <path d="M3 12h18" />
    </svg>
  );
}

function LanguageIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="8.4" />
      <path d="M3.6 12h16.8" />
      <path d="M12 3.6c-2.8 2.2-2.8 14.6 0 16.8" />
      <path d="M12 3.6c2.8 2.2 2.8 14.6 0 16.8" />
    </svg>
  );
}

function PricingIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="2.2" />
      <path d="M2.6 9.6h18.8" />
      <path d="M6 14.4h4" />
    </svg>
  );
}

function SettingsIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="3.1" />
      <path d="M12 3.6v2.2M12 18.2v2.2M20.4 12h-2.2M5.8 12H3.6M17.5 6.5l-1.5 1.5M8 16l-1.5 1.5M17.5 17.5 16 16M8 8 6.5 6.5" />
    </svg>
  );
}

function PersonalizationIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3.6a8.4 8.4 0 1 0 0 16.8c1.2 0 1.9-.9 1.9-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.3 0-1 .8-1.8 1.8-1.8h1.9a3.4 3.4 0 0 0 3.4-3.4c0-4-3.6-7-8-7Z" />
      <circle cx="8.2" cy="10.4" r="1" />
      <circle cx="12" cy="7.8" r="1" />
      <circle cx="15.8" cy="10.4" r="1" />
    </svg>
  );
}

/**
 * Логика выхода, общая для всех мест, откуда можно выйти: попапа аккаунта,
 * его развёрнутого вида и футера единого сайдбара. Вынесена из компонента,
 * чтобы у выхода из футера сайдбара (см. MobileShellHeader) было в точности
 * то же поведение — разрыв сеанса и уход с /trips, — а не своя копия с
 * рассинхроном при следующей правке одной из двух.
 */
export function useSignOutAction(onClose: () => void): () => void {
  const { signOut } = useAuth();
  const { path } = useRoute();
  return () => {
    onClose();
    void (async () => {
      await signOut();
      // Список поездок — экран вошедшего; гостю там больше нечего делать.
      // С любого другого экрана (в том числе с /pricing — он доступен и гостю)
      // никуда не уводим: выход не должен прерывать то, чем человек занят.
      if (path === '/trips') navigate('/');
    })();
  };
}

export function AccountMenu({
  onClose,
  onOpenSettings,
  onOpenPersonalization,
  inline = false,
  hideSignOut = false,
}: AccountMenuProps): ReactElement {
  const t = useT();
  const { user, isAdmin, requestSignIn } = useAuth();
  const trips = useTrips();
  const handleSignOut = useSignOutAction(onClose);

  // Гостю useTrips() всегда отдаёт пустой список — своего сервера у него нет,
  // а локальное хранилище провайдер поездок не читает. Отличить «черновик
  // пуст» от «в нём уже есть блоки» здесь нечем, а гостевая работа в любом
  // случае лежит одной-единственной поездкой — поэтому показываем 1.
  const tripsCount = user ? trips.trips.length : 1;
  const tripsHint = user ? t('account.menu.trips.hint') : t('account.menu.trips.hint.guest');

  function handleTrips(): void {
    if (!user) {
      onClose();
      requestSignIn();
      return;
    }
    onClose();
    navigate('/trips');
  }

  function handlePricing(): void {
    onClose();
    navigate('/pricing');
  }

  function handleAdmin(): void {
    onClose();
    navigate('/admin');
  }

  return (
    <div
      className={`${styles.menu} ${inline ? styles.inline : ''}`}
      aria-label={t('account.title')}
    >
      {/* Это не командное меню (role="menu" тут не было бы честным: стрелок,
          Home/End и возврата фокуса роверинг-табиндексом здесь нет) — обычная
          выпадающая панель с настройками и ссылками. Табуляция ходит по её
          кнопкам и полю выбора языка сама, безо всякой ручной клавиатурной
          механики. Строка языка — просто div: выбор в ней не закрывает
          панель, потому что это настройка, а не переход. Своей постоянной
          строки-подсказки под ней нет: селект уже несёт и aria-label, и
          title (см. i18n/LanguagePicker.tsx) — вторая строка с тем же
          смыслом была бы чистым дублированием. */}
      <div className={styles.row} title={t('account.menu.language.hint')}>
        <span className={styles.rowLabel}>
          <LanguageIcon />
          <span>{t('account.menu.language')}</span>
        </span>
        <LanguagePicker />
      </div>

      {/* Подсказка есть у каждого пункта в двух видах: `title` — на случай
          наведения мышью, и постоянная приглушённая строка под названием —
          потому что title не всплывает от касания, а меню целиком живёт и в
          мобильном сайдбаре. Дублирование в пользу мобильного экрана: там
          подсказки взяться неоткуда, кроме как с самой кнопки. */}
      <button
        type="button"
        data-menu-close
        className={styles.item}
        title={tripsHint}
        onClick={handleTrips}
      >
        <span className={styles.itemMain}>
          <span className={styles.itemLabel}>
            <TripsIcon />
            <span>{t('trips.title')}</span>
          </span>
          <span className={`mono ${styles.itemCount}`}>{tripsCount}</span>
        </span>
        <span className={styles.itemHint}>{tripsHint}</span>
      </button>

      <button
        type="button"
        data-menu-close
        className={styles.item}
        title={t('account.menu.pricing.hint')}
        onClick={handlePricing}
      >
        <span className={styles.itemMain}>
          <span className={styles.itemLabel}>
            <PricingIcon />
            <span>{t('account.menu.pricing')}</span>
          </span>
        </span>
        <span className={styles.itemHint}>{t('account.menu.pricing.hint')}</span>
      </button>

      {/* Раньше ссылка на админку жила в AccountModal и в бургере TripsPage —
          оба места пропали (модалка удалена, бургер тех переделан), а адрес
          /admin остался рабочим. Пункт виден только тем, у кого есть право. */}
      {isAdmin && (
        <button
          type="button"
          data-menu-close
          className={styles.item}
          title={t('account.menu.admin.hint')}
          onClick={handleAdmin}
        >
          <span className={styles.itemMain}>
            <span className={styles.itemLabel}>
              <AdminIcon />
              <span>{t('account.admin')}</span>
            </span>
          </span>
          <span className={styles.itemHint}>{t('account.menu.admin.hint')}</span>
        </button>
      )}

      {user && (
        <button
          type="button"
          className={styles.item}
          title={t('account.menu.settings.hint')}
          onClick={onOpenSettings}
        >
          <span className={styles.itemMain}>
            <span className={styles.itemLabel}>
              <SettingsIcon />
              <span>{t('account.menu.settings')}</span>
            </span>
          </span>
          <span className={styles.itemHint}>{t('account.menu.settings.hint')}</span>
        </button>
      )}

      <button
        type="button"
        className={styles.item}
        title={t('account.menu.person.hint')}
        onClick={onOpenPersonalization}
      >
        <span className={styles.itemMain}>
          <span className={styles.itemLabel}>
            <PersonalizationIcon />
            <span>{t('person.title')}</span>
          </span>
        </span>
        <span className={styles.itemHint}>{t('account.menu.person.hint')}</span>
      </button>

      {/* Вход — такое же действие, как выход, и стоит там же, где тот:
          последним пунктом, отбитым волоском от списка настроек. Раньше он
          занимал строку «Настроек» в середине меню, и единственное действие,
          которое гостю вообще доступно, пряталось между «Прайсингом» и
          «Персонализацией» — а в мобильном сайдбаре, где у вошедшего выход
          прибит к низу панели, «Войти» оказывалось где-то посреди списка. */}
      {!user && (
        <button
          type="button"
          data-menu-close
          className={`${styles.item} ${styles.action}`}
          onClick={() => {
            onClose();
            requestSignIn();
          }}
        >
          <span className={styles.itemMain}>{t('auth.signin')}</span>
        </button>
      )}

      {/* Выход стоит последним и отдельно от волоска: это не настройка, а
          действие, которое рвёт сеанс, и путать его в общий ряд с языком и
          персонализацией нельзя. Показывается только вошедшему — гостю
          соответствующим действием служит «Войти» выше.
          hideSignOut прячет и эту строку: в едином сайдбаре (MobileShellHeader)
          выход стоит своим футером, прибитым к низу панели, — второй раз тем
          же действием в конце списка он был бы лишним, а с длинным списком
          пунктов и уезжал бы куда-то в середину прокрутки. */}
      {user && !hideSignOut && (
        <button
          type="button"
          data-menu-close
          className={`${styles.item} ${styles.action}`}
          title={t('account.menu.signout.hint')}
          onClick={handleSignOut}
        >
          <span className={styles.itemMain}>
            <span className={styles.itemLabel}>
              <SignOutIcon />
              <span>{t('auth.signout')}</span>
            </span>
          </span>
          <span className={styles.itemHint}>{t('account.menu.signout.hint')}</span>
        </button>
      )}
    </div>
  );
}
